import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Brand } from "@/components/Brand";
import { ViewNav } from "@/components/ViewNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Sun, BatteryCharging, Zap, Building2, Settings, Loader2, Activity, Radio,
  ArrowDown, ArrowUp, Leaf, Plug, DollarSign, TrendingUp,
} from "lucide-react";
import {
  ComposedChart, Area, Bar, Line, LineChart, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";

const NZD = (v) => `$${v.toFixed(2)}`;
const kw = (v) => `${v.toFixed(2)} kW`;

const exportRateNow = (cfg, d) => {
  const day = d.getDay();
  if (day === 0 || day === 6) return cfg.export_weekend_rate;
  const h = d.getHours();
  const peak = (h >= 7 && h < 11) || (h >= 17 && h < 21);
  return peak ? cfg.export_peak_rate : cfg.export_offpeak_rate;
};
const ratePeriodLabel = (d) => {
  const day = d.getDay();
  if (day === 0 || day === 6) return "Weekend";
  const h = d.getHours();
  return (h >= 7 && h < 11) || (h >= 17 && h < 21) ? "Peak" : "Off-peak";
};

const SIM_HOURS_PER_TICK = 0.08; // ~5 min of simulated time per 2s tick
const MIN_SOC = 8;

const simSolar = (capacity, d) => {
  const h = d.getHours() + d.getMinutes() / 60 + d.getSeconds() / 3600;
  const sunrise = 6.5, sunset = 19.5;
  if (h <= sunrise || h >= sunset) return 0;
  const x = (h - sunrise) / (sunset - sunrise);
  const bell = Math.sin(Math.PI * x);
  const noise = 0.88 + Math.random() * 0.17;
  return Math.max(0, capacity * 0.9 * bell * noise);
};
const simLoad = (d) => {
  const h = d.getHours();
  const business = h >= 7 && h <= 18;
  const base = 1.1 + Math.random() * 0.5;
  const office = business ? 1.6 + Math.random() * 2.4 : Math.random() * 0.6;
  return base + office;
};

// --- flow diagram ---
const SOLAR = { x: 150, y: 110 };
const GRID = { x: 750, y: 110 };
const HUB = { x: 450, y: 245 };
const LOADS = { x: 450, y: 400 };

const Flow = ({ from, to, active, color, id }) => {
  const d = `M ${from.x} ${from.y} L ${to.x} ${to.y}`;
  return (
    <g data-testid={`flow-${id}`} data-active={active ? "true" : "false"}>
      <line
        x1={from.x} y1={from.y} x2={to.x} y2={to.y}
        stroke={active ? color : "#E2E8F0"} strokeWidth={active ? 4 : 2.5}
        strokeLinecap="round" strokeDasharray={active ? "0" : "6 6"}
      />
      {active && [0, 1, 2].map((i) => (
        <circle key={`${id}-${i}`} r="5" fill={color}>
          <animateMotion dur="1.5s" begin={`${i * 0.5}s`} repeatCount="indefinite" path={d} />
        </circle>
      ))}
    </g>
  );
};

const NodeCard = ({ x, y, children, testid, tone = "border-slate-200" }) => (
  <div
    data-testid={testid}
    className={`absolute -translate-x-1/2 -translate-y-1/2 w-[190px] bg-white rounded-xl border ${tone} shadow-md px-4 py-3`}
    style={{ left: x, top: y }}
  >
    {children}
  </div>
);

const SocBar = ({ soc, charging }) => (
  <div className="mt-2">
    <div className="h-2.5 w-full bg-slate-100 rounded-full overflow-hidden">
      <div
        className={`h-full rounded-full transition-all duration-700 ${charging === "charging" ? "bg-emerald-500" : charging === "discharging" ? "bg-amber-500" : "bg-slate-400"}`}
        style={{ width: `${Math.max(2, soc)}%` }}
      />
    </div>
  </div>
);

const Diagram = ({ m, cfg }) => (
  <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-2">
    <div className="relative mx-auto" style={{ width: 900, height: 480 }}>
      <svg viewBox="0 0 900 480" className="absolute inset-0 h-full w-full">
        <Flow id="solar-hub" from={SOLAR} to={HUB} active={m.solar > 0.05} color="#F59E0B" />
        <Flow id="hub-loads" from={HUB} to={LOADS} active={m.load > 0.05} color="#2563EB" />
        <Flow
          id="grid"
          from={m.grid >= 0 ? HUB : GRID}
          to={m.grid >= 0 ? GRID : HUB}
          active={Math.abs(m.grid) > 0.05}
          color={m.grid >= 0 ? "#10B981" : "#EF4444"}
        />
      </svg>

      <NodeCard x={SOLAR.x} y={SOLAR.y} testid="node-solar" tone="border-amber-200">
        <div className="flex items-center gap-2 text-amber-600">
          <Sun className="h-5 w-5" />
          <span className="text-xs font-semibold uppercase tracking-wider">Solar Array</span>
        </div>
        <p className="font-display text-2xl font-bold text-slate-900 font-mono-nums mt-1" data-testid="metric-solar-kw">{kw(m.solar)}</p>
        <p className="text-xs text-slate-500">of {cfg.solar_capacity_kw} kW · {m.solarKwhToday.toFixed(1)} kWh today</p>
      </NodeCard>

      <NodeCard x={GRID.x} y={GRID.y} testid="node-grid" tone={m.grid >= 0 ? "border-emerald-200" : "border-red-200"}>
        <div className={`flex items-center gap-2 ${m.grid >= 0 ? "text-emerald-600" : "text-red-600"}`}>
          <Plug className="h-5 w-5" />
          <span className="text-xs font-semibold uppercase tracking-wider">Grid</span>
        </div>
        <p className="font-display text-2xl font-bold text-slate-900 font-mono-nums mt-1 flex items-center gap-1" data-testid="metric-grid-kw">
          {m.grid >= 0 ? <ArrowUp className="h-4 w-4 text-emerald-600" /> : <ArrowDown className="h-4 w-4 text-red-600" />}
          {kw(Math.abs(m.grid))}
        </p>
        <p className="text-xs text-slate-500">{m.grid >= 0 ? "exporting" : "importing"} · {cfg.retailer}</p>
      </NodeCard>

      <NodeCard x={HUB.x} y={HUB.y} testid="node-battery" tone="border-slate-300">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-slate-700">
            <BatteryCharging className="h-5 w-5" />
            <span className="text-xs font-semibold uppercase tracking-wider">SigenStor</span>
          </div>
          <span
            data-testid="battery-status"
            className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${m.charge === "charging" ? "bg-emerald-100 text-emerald-700" : m.charge === "discharging" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}
          >
            {m.charge === "charging" ? "CHARGING" : m.charge === "discharging" ? "DISCHARGING" : "IDLE"}
          </span>
        </div>
        <p className="font-display text-3xl font-bold text-slate-900 font-mono-nums mt-1" data-testid="metric-soc">{m.soc.toFixed(0)}%</p>
        <SocBar soc={m.soc} charging={m.charge} />
        <p className="text-xs text-slate-500 mt-1.5">
          {(cfg.battery_capacity_kwh * m.soc / 100).toFixed(1)} / {cfg.battery_capacity_kwh} kWh
          {m.battery !== 0 && ` · ${kw(Math.abs(m.battery))}`}
        </p>
      </NodeCard>

      <NodeCard x={LOADS.x} y={LOADS.y} testid="node-loads" tone="border-blue-200">
        <div className="flex items-center gap-2 text-blue-600">
          <Building2 className="h-5 w-5" />
          <span className="text-xs font-semibold uppercase tracking-wider">Facility Loads</span>
        </div>
        <p className="font-display text-2xl font-bold text-slate-900 font-mono-nums mt-1" data-testid="metric-load-kw">{kw(m.load)}</p>
        <p className="text-xs text-slate-500">Razza site consumption</p>
      </NodeCard>
    </div>
  </div>
);

const StatTile = ({ icon: Icon, label, value, sub, tone, testid }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-testid={testid}>
    <div className={`h-9 w-9 rounded-md flex items-center justify-center mb-2 ${tone}`}><Icon className="h-5 w-5" /></div>
    <p className="text-xs font-mono-nums uppercase tracking-wider text-slate-500">{label}</p>
    <p className="font-display text-xl font-bold text-slate-900 font-mono-nums leading-tight">{value}</p>
    {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
  </div>
);

const ConfigDialog = ({ cfg, onSaved, isAdmin }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(cfg);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(cfg); }, [cfg]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const nums = {
        modbus_port: Number(form.modbus_port),
        solar_capacity_kw: Number(form.solar_capacity_kw),
        inverter_power_kw: Number(form.inverter_power_kw),
        battery_capacity_kwh: Number(form.battery_capacity_kwh),
        export_peak_rate: Number(form.export_peak_rate),
        export_offpeak_rate: Number(form.export_offpeak_rate),
        export_weekend_rate: Number(form.export_weekend_rate),
        import_rate: Number(form.import_rate),
      };
      if (Object.values(nums).some((n) => !Number.isFinite(n))) {
        toast.error("Please enter valid numbers for all specs and rates");
        setSaving(false);
        return;
      }
      const body = {
        modbus_ip: form.modbus_ip,
        simulation_mode: !!form.simulation_mode,
        retailer: form.retailer,
        ...nums,
      };
      const updated = await api.updateSigenConfig(body);
      toast.success("SigenStor configuration saved");
      onSaved(updated);
      setOpen(false);
    } catch {
      toast.error("Could not save configuration");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="h-9 gap-2 border-slate-300" data-testid="sigen-config-open">
          <Settings className="h-4 w-4" /> Configure
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-auto" data-testid="sigen-config-dialog">
        <DialogHeader>
          <DialogTitle>SigenStor Configuration</DialogTitle>
          <DialogDescription>
            {isAdmin ? "Set the on-site Modbus TCP connection and tariffs. Simulation mode drives the live view until installation is complete." : "View-only. Ask an admin to change these settings."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
            <div>
              <p className="font-semibold text-sm text-slate-800">Simulation Mode</p>
              <p className="text-xs text-slate-500">Live-updating demo metrics while offline</p>
            </div>
            <Switch
              data-testid="sigen-sim-toggle"
              checked={!!form.simulation_mode}
              onCheckedChange={(v) => set("simulation_mode", v)}
              disabled={!isAdmin}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Modbus TCP IP</Label>
              <Input data-testid="sigen-ip-input" value={form.modbus_ip || ""} onChange={(e) => set("modbus_ip", e.target.value)} disabled={!isAdmin} placeholder="192.168.1.150" />
            </div>
            <div className="space-y-1.5">
              <Label>Port</Label>
              <Input data-testid="sigen-port-input" type="number" value={form.modbus_port || ""} onChange={(e) => set("modbus_port", e.target.value)} disabled={!isAdmin} placeholder="502" />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label>Solar kW</Label>
              <Input type="number" step="0.01" value={form.solar_capacity_kw || ""} onChange={(e) => set("solar_capacity_kw", e.target.value)} disabled={!isAdmin} />
            </div>
            <div className="space-y-1.5">
              <Label>Inverter kW</Label>
              <Input type="number" step="0.1" value={form.inverter_power_kw || ""} onChange={(e) => set("inverter_power_kw", e.target.value)} disabled={!isAdmin} />
            </div>
            <div className="space-y-1.5">
              <Label>Battery kWh</Label>
              <Input type="number" step="0.01" value={form.battery_capacity_kwh || ""} onChange={(e) => set("battery_capacity_kwh", e.target.value)} disabled={!isAdmin} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Retailer</Label>
            <Input value={form.retailer || ""} onChange={(e) => set("retailer", e.target.value)} disabled={!isAdmin} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Export · Peak ($/kWh)</Label>
              <Input data-testid="sigen-peak-input" type="number" step="0.01" value={form.export_peak_rate ?? ""} onChange={(e) => set("export_peak_rate", e.target.value)} disabled={!isAdmin} />
            </div>
            <div className="space-y-1.5">
              <Label>Export · Off-peak ($/kWh)</Label>
              <Input type="number" step="0.01" value={form.export_offpeak_rate ?? ""} onChange={(e) => set("export_offpeak_rate", e.target.value)} disabled={!isAdmin} />
            </div>
            <div className="space-y-1.5">
              <Label>Export · Weekend ($/kWh)</Label>
              <Input type="number" step="0.01" value={form.export_weekend_rate ?? ""} onChange={(e) => set("export_weekend_rate", e.target.value)} disabled={!isAdmin} />
            </div>
            <div className="space-y-1.5">
              <Label>Import ($/kWh)</Label>
              <Input type="number" step="0.01" value={form.import_rate ?? ""} onChange={(e) => set("import_rate", e.target.value)} disabled={!isAdmin} />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
          {isAdmin && (
            <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-700 gap-2" data-testid="sigen-config-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const RANGES = [
  { d: 7, label: "7d" },
  { d: 30, label: "30d" },
  { d: 90, label: "90d" },
];

const EnergyHistory = () => {
  const [days, setDays] = useState(30);
  const [rows, setRows] = useState(null);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    let active = true;
    Promise.all([api.getSigenHistory(days), api.getSigenHistorySummary(days)])
      .then(([h, s]) => { if (active) { setRows(h); setSummary(s); } })
      .catch(() => { if (active) { setRows([]); setSummary(null); } });
    return () => { active = false; };
  }, [days]);

  let cum = 0;
  const chart = (rows || []).map((r) => {
    cum += r.savings;
    return {
      date: r.date.slice(5),
      solar: r.solar_kwh,
      export: r.export_kwh,
      self: r.self_consumption_kwh,
      savings: r.savings,
      cumSavings: Number(cum.toFixed(2)),
    };
  });
  const t = summary?.totals;

  return (
    <section className="space-y-4" data-testid="energy-history">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-emerald-600" /> Energy History &amp; Savings
          </h3>
          <p className="text-sm text-slate-500">Daily solar generation, grid export and estimated savings over time.</p>
        </div>
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1" data-testid="history-range-toggle">
          {RANGES.map((r) => (
            <button
              key={r.d}
              data-testid={`history-range-${r.d}`}
              onClick={() => setDays(r.d)}
              className={`px-3 py-1.5 rounded-md text-sm font-semibold transition-colors ${days === r.d ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {t && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatTile icon={Sun} label="Solar Generated" value={`${t.solar_kwh.toFixed(0)} kWh`} sub={`last ${summary.days} days`} tone="bg-amber-100 text-amber-700" testid="hist-solar" />
          <StatTile icon={ArrowUp} label="Grid Export" value={`${t.export_kwh.toFixed(0)} kWh`} sub={NZD(t.export_earnings)} tone="bg-emerald-100 text-emerald-700" testid="hist-export" />
          <StatTile icon={BatteryCharging} label="Battery Cycles" value={t.battery_cycles.toFixed(1)} sub={`${t.self_consumption_kwh.toFixed(0)} kWh self-used`} tone="bg-blue-100 text-blue-700" testid="hist-cycles" />
          <StatTile icon={DollarSign} label="Est. Savings" value={NZD(t.savings)} sub={`~${NZD(summary.avg_daily_savings)}/day`} tone="bg-green-100 text-green-700" testid="hist-savings" />
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
          <p className="text-sm font-semibold text-slate-700 mb-3">Daily generation &amp; export (kWh)</p>
          <div className="h-64 w-full" data-testid="history-generation-chart">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chart} margin={{ top: 5, right: 8, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94A3B8" }} interval="preserveStartEnd" minTickGap={20} />
                <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Area type="monotone" dataKey="solar" name="Solar" fill="#FEF3C7" stroke="#F59E0B" strokeWidth={2} />
                <Bar dataKey="export" name="Exported" fill="#10B981" radius={[2, 2, 0, 0]} barSize={10} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
          <p className="text-sm font-semibold text-slate-700 mb-3">Cumulative savings ($NZD)</p>
          <div className="h-64 w-full" data-testid="history-savings-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chart} margin={{ top: 5, right: 8, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94A3B8" }} interval="preserveStartEnd" minTickGap={20} />
                <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} formatter={(v) => NZD(Number(v))} />
                <Line type="monotone" dataKey="cumSavings" name="Cumulative savings" stroke="#16A34A" strokeWidth={2.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      <p className="text-xs text-slate-400">Figures are modelled estimates (seasonal) until live on-site telemetry is connected; the daily rollup keeps extending this history automatically.</p>
    </section>
  );
};

export default function SigenStor() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [cfg, setCfg] = useState(null);
  const [m, setM] = useState(null);
  const sim = useRef({ soc: 58, exportKwh: 0, importKwh: 0, solarKwhToday: 0, earnings: 0 });

  useEffect(() => { api.getSigenConfig().then(setCfg).catch(() => setCfg(null)); }, []);

  const tick = useCallback(() => {
    if (!cfg) return;
    const now = new Date();
    const cap = cfg.solar_capacity_kw || 12.24;
    const inv = cfg.inverter_power_kw || 10;
    const batt = cfg.battery_capacity_kwh || 18.08;
    const s = sim.current;

    const solar = simSolar(cap, now);
    const load = simLoad(now);
    let surplus = solar - load;
    let battery = 0; // + discharging, - charging
    let grid = 0;    // + export, - import
    let charge = "idle";

    if (surplus > 0.02) {
      const roomKwh = batt * (1 - s.soc / 100);
      const maxChargeKw = Math.min(inv, roomKwh / SIM_HOURS_PER_TICK);
      const chargeKw = Math.min(surplus, maxChargeKw);
      battery = -chargeKw;
      grid = surplus - chargeKw; // export remainder
      s.soc = Math.min(100, s.soc + (chargeKw * SIM_HOURS_PER_TICK / batt) * 100);
      if (chargeKw > 0.03) charge = "charging";
    } else if (surplus < -0.02) {
      const deficit = -surplus;
      const availKwh = batt * ((s.soc - MIN_SOC) / 100);
      const maxDischargeKw = Math.min(inv, Math.max(0, availKwh) / SIM_HOURS_PER_TICK);
      const dischargeKw = Math.min(deficit, maxDischargeKw);
      battery = dischargeKw;
      grid = -(deficit - dischargeKw); // import remainder
      s.soc = Math.max(MIN_SOC, s.soc - (dischargeKw * SIM_HOURS_PER_TICK / batt) * 100);
      if (dischargeKw > 0.03) charge = "discharging";
    }

    const rate = exportRateNow(cfg, now);
    if (grid > 0) { s.exportKwh += grid * SIM_HOURS_PER_TICK; s.earnings += grid * SIM_HOURS_PER_TICK * rate; }
    if (grid < 0) { s.importKwh += -grid * SIM_HOURS_PER_TICK; }
    s.solarKwhToday += solar * SIM_HOURS_PER_TICK;

    setM({
      solar, load, battery, grid, charge, soc: s.soc,
      exportKwh: s.exportKwh, importKwh: s.importKwh, solarKwhToday: s.solarKwhToday,
      earnings: s.earnings, rate, period: ratePeriodLabel(now), ts: now,
    });
  }, [cfg]);

  useEffect(() => {
    if (!cfg || !cfg.simulation_mode) { setM(null); return; }
    tick();
    const id = setInterval(tick, 2000);
    return () => clearInterval(id);
  }, [cfg, tick]);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-200 shadow-sm">
        <div className="max-w-[1500px] mx-auto px-5 py-3 flex items-center justify-between gap-4">
          <Brand subtitle="Energy · SigenStor" />
          <ViewNav />
        </div>
      </header>

      <main className="max-w-[1500px] mx-auto px-5 py-6 space-y-6">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Sun className="h-6 w-6 text-amber-500" /> SigenStor Solar &amp; Battery
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              {cfg ? `${cfg.solar_capacity_kw} kW solar · ${cfg.battery_model} (${cfg.battery_capacity_kwh} kWh) · ${cfg.inverter_model}` : "Loading system..."} — Razza site
            </p>
          </div>
          <div className="flex items-center gap-2">
            {cfg && (
              <span
                data-testid="sigen-connection-status"
                className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 h-9 rounded-md border ${cfg.simulation_mode ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}
              >
                {cfg.simulation_mode ? <><Activity className="h-3.5 w-3.5" /> Simulation Mode</> : <><Radio className="h-3.5 w-3.5" /> Awaiting on-site link</>}
              </span>
            )}
            {cfg && <ConfigDialog cfg={cfg} onSaved={setCfg} isAdmin={isAdmin} />}
          </div>
        </div>

        {!cfg ? (
          <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading system...</div>
        ) : !cfg.simulation_mode ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="sigen-offline">
            <Radio className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <h3 className="font-display text-lg font-bold text-slate-800">No live connection</h3>
            <p className="text-sm text-slate-500 max-w-md mx-auto mt-1">
              Simulation Mode is off. Live metrics read the SigenStor over Modbus TCP at <span className="font-mono-nums">{cfg.modbus_ip}:{cfg.modbus_port}</span>, which is only reachable from the on-site network once installation is complete. Turn Simulation Mode on to preview the dashboard.
            </p>
          </div>
        ) : m ? (
          <>
            <Diagram m={m} cfg={cfg} />

            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
              <StatTile icon={Sun} label="Solar Now" value={kw(m.solar)} sub={`${m.solarKwhToday.toFixed(1)} kWh today`} tone="bg-amber-100 text-amber-700" testid="tile-solar" />
              <StatTile icon={BatteryCharging} label="Battery SoC" value={`${m.soc.toFixed(0)}%`} sub={m.charge} tone="bg-emerald-100 text-emerald-700" testid="tile-soc" />
              <StatTile icon={Building2} label="Load" value={kw(m.load)} sub="facility draw" tone="bg-blue-100 text-blue-700" testid="tile-load" />
              <StatTile icon={m.grid >= 0 ? ArrowUp : ArrowDown} label={m.grid >= 0 ? "Exporting" : "Importing"} value={kw(Math.abs(m.grid))} sub={`${m.period} · ${(m.rate * 100).toFixed(0)}c/kWh`} tone={m.grid >= 0 ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"} testid="tile-grid" />
              <StatTile icon={DollarSign} label="Export Earnings" value={NZD(m.earnings)} sub={`${m.exportKwh.toFixed(1)} kWh exported`} tone="bg-green-100 text-green-700" testid="tile-earnings" />
              <StatTile icon={Leaf} label="Self-powered" value={`${Math.min(100, Math.round(((m.solar + Math.max(m.battery, 0)) / Math.max(m.load, 0.1)) * 100))}%`} sub="of load from solar+battery" tone="bg-lime-100 text-lime-700" testid="tile-self" />
            </div>

            <div className="text-xs text-slate-400 flex items-center gap-1.5">
              <Zap className="h-3.5 w-3.5" /> Live simulated data · updates every 2s · {cfg.retailer} feed-in tariffs (peak {(cfg.export_peak_rate * 100).toFixed(0)}c / off-peak {(cfg.export_offpeak_rate * 100).toFixed(0)}c / import {(cfg.import_rate * 100).toFixed(0)}c per kWh). Real-time values appear once the SigenStor is connected on site.
            </div>
          </>
        ) : null}

        {cfg && <EnergyHistory />}
      </main>
    </div>
  );
}
